import { useForm } from "@formiz/core";
import { LorebookFlow, NotificationType, useLorebook, useTranslation } from "@next-platform/client-common-core";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { PageLayout, PageSize } from "@/components";
import PageStaticMetaTags from "@/components/common/PageStaticMetaTags";
import useToast from "@/hooks/useToast";

import ReportFooter from "./components/ReportFooter";
import ReportForm, { ItemType } from "./components/ReportForm";
import ReportHeader from "./components/ReportHeader";

const ReportLorebook = () => {
  const reportForm = useForm();
  const { isValid, values } = reportForm;
  const { lorebookId } = useParams();
  const [stateSuccessMessage, setStateSuccessMessage] = useState<string | null>(null);
  const [stateIsFormSubmitted, setStateIsFormSubmitted] = useState(false);
  const [stateIsSubmitAttempted, setStateIsSubmitAttempted] = useState(false);
  const hasFormChanges = useMemo(() => Object.values(values).some((value) => value !== null && value !== undefined && value !== ""), [values]);
  const { t } = useTranslation();
  const { displayToast } = useToast();
  const { lorebookData, getLorebookData, onUserReportLorebook, loadingType, errorMessage: submitError, isLoading } = useLorebook();
  const isSubmitting = loadingType === LorebookFlow.REPORT_LOREBOOK;

  useEffect(() => {
    if (lorebookId) {
      getLorebookData(lorebookId).catch(() => undefined);
    }
  }, [lorebookId, getLorebookData]);

  const initialValues = useMemo(
    () => ({
      userIssueType: "",
      userReportDetails: "",
      userExpectedResolution: "",
    }),
    [],
  );

  const handleReportSuccess = useCallback(() => {
    const successMessage = t("lorebook:reportPage.form.successMessage");
    setStateSuccessMessage(successMessage);
    setStateIsFormSubmitted(true);
    displayToast({ message: successMessage, type: NotificationType.success });
  }, [t, displayToast]);

  const submitForm = useCallback(() => {
    setStateIsSubmitAttempted(true);
    const payload: Parameters<typeof onUserReportLorebook>[1] = {
      userIssueType: reportForm.values.userIssueType,
      userReportDetails: reportForm.values.userReportDetails,
      userExpectedResolution: reportForm.values.userExpectedResolution,
    };
    if (isValid) {
      onUserReportLorebook(lorebookId ?? "", payload)
        .then(({ error }) => {
          if (error) return;
          handleReportSuccess();
        })
        .catch(() => undefined);
    }
  }, [handleReportSuccess, lorebookId, onUserReportLorebook, reportForm, isValid]);

  return (
    <PageLayout size={PageSize.Medium}>
      <PageStaticMetaTags pageId="report" />
      <ReportHeader itemType={ItemType.Lorebook} />
      <ReportForm
        reportForm={reportForm}
        initialValues={initialValues}
        submitForm={submitForm}
        itemId={lorebookId ?? ""}
        itemType={ItemType.Lorebook}
        itemData={lorebookData}
        isLoading={isLoading}
        isSubmitAttempted={stateIsSubmitAttempted}
        setStateIsSubmitAttempted={setStateIsSubmitAttempted}
      />
      <ReportFooter
        submitForm={submitForm}
        successMessage={stateSuccessMessage}
        isFormSubmitted={stateIsFormSubmitted}
        isValid={isValid}
        hasFormChanges={hasFormChanges}
        isSubmitting={isSubmitting}
        submitError={submitError}
        itemType={ItemType.Lorebook}
        isSubmitAttempted={stateIsSubmitAttempted}
      />
    </PageLayout>
  );
};

export default ReportLorebook;
